import * as React from "react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { ReasonField } from "@/components/ReasonField";

const PLAN_OPTIONS = [
  { value: "trial_7d", label: "Trial 7 дней" },
  { value: "premium_1m", label: "Premium 1 мес" },
  { value: "premium_3m", label: "Premium 3 мес" },
] as const;

interface GrantModalProps {
  open: boolean;
  userId: string;
  onGrant: (params: { plan: string; grantUntil: string; reason: string }) => Promise<void>;
  onClose: () => void;
}

function isNetworkError(err: unknown): boolean {
  return err instanceof TypeError && err.message.toLowerCase().includes("fetch");
}

export function GrantModal({
  open,
  userId,
  onGrant,
  onClose,
}: GrantModalProps): React.JSX.Element {
  const [plan, setPlan] = React.useState("premium_1m");
  const [grantUntil, setGrantUntil] = React.useState("");
  const [reason, setReason] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  React.useEffect(() => {
    if (!open) {
      setPlan("premium_1m");
      setGrantUntil("");
      setReason("");
      setSubmitting(false);
      setErrorMessage("");
    }
  }, [open]);

  const canSubmit = reason.trim().length > 0 && grantUntil.trim().length > 0 && !submitting;

  async function handleSubmit(): Promise<void> {
    setSubmitting(true);
    setErrorMessage("");
    try {
      await onGrant({ plan, grantUntil, reason });
      onClose();
    } catch (err: unknown) {
      if (isNetworkError(err)) {
        setErrorMessage("Нет соединения, повторите");
      } else {
        const bffErr = err as { message?: string } | null;
        setErrorMessage(bffErr?.message ?? "Ошибка сервера. Попробуйте ещё раз.");
      }
      setSubmitting(false);
    }
  }

  return (
    <Dialog open={open} onOpenChange={(isOpen) => { if (!isOpen) onClose(); }}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Выдать компенсацию</DialogTitle>
        </DialogHeader>

        <div className="py-1 flex flex-col gap-3">
          <div className="flex flex-col gap-1.5">
            <label htmlFor={`grant-plan-${userId}`} className="text-[12px] font-[500] text-[var(--fg)]">
              План
            </label>
            <select
              id={`grant-plan-${userId}`}
              value={plan}
              onChange={(e) => setPlan(e.target.value)}
              className="w-full font-[var(--font)] text-[13px] px-3 py-2 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] focus:outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent-weak)]"
            >
              {PLAN_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>{o.label}</option>
              ))}
            </select>
          </div>

          <div className="flex flex-col gap-1.5">
            <label htmlFor={`grant-until-${userId}`} className="text-[12px] font-[500] text-[var(--fg)]">
              Действует до <span className="text-[var(--danger)] ml-0.5">*</span>
            </label>
            <input
              id={`grant-until-${userId}`}
              type="date"
              value={grantUntil}
              onChange={(e) => setGrantUntil(e.target.value)}
              required
              className="w-full font-[var(--font)] text-[13px] px-3 py-2 rounded-[var(--radius-sm)] border border-[var(--border)] bg-[var(--surface)] text-[var(--fg)] focus:outline-none focus:border-[var(--accent)] focus:ring-1 focus:ring-[var(--accent-weak)]"
            />
          </div>

          <ReasonField
            id={`grant-reason-${userId}`}
            label="Причина"
            value={reason}
            onChange={setReason}
            required
            placeholder="Укажите причину выдачи компенсации..."
          />

          {errorMessage && (
            <div
              role="alert"
              className="flex items-start gap-2 px-3.5 py-2.5 bg-[rgba(196,58,42,0.08)] border border-[rgba(196,58,42,0.2)] rounded-[var(--radius-sm)] text-[12px] text-[var(--danger)]"
            >
              <span>✕</span>
              <span>{errorMessage}</span>
            </div>
          )}
        </div>

        <div className="flex items-center justify-between pt-4 mt-2 border-t border-[var(--border)]">
          <span className="text-[11px] text-[var(--muted)] flex-1 leading-relaxed pr-3">
            Действие будет зафиксировано в Audit Log
          </span>
          <div className="flex gap-2 flex-shrink-0">
            <Button type="button" variant="ghost" onClick={onClose} disabled={submitting}>
              Отмена
            </Button>
            <Button
              type="button"
              variant="default"
              onClick={() => { void handleSubmit(); }}
              disabled={!canSubmit}
            >
              {submitting ? "Загрузка..." : "Выдать"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
